import { cn } from 'src/shadcn/lib/utils';
import * as React from 'react';
import { Icon } from 'src/Components/Shared/Icon';
import {
  isNever,
  isNullOrUndefinedOrEmptyString,
  onEnterPress,
} from 'src/utils/utils';
import * as uuid from 'uuid';

export interface IBaseInternalInputProps<T> {
  disabled?: boolean;
  label?: string;
  value: T | undefined;
  onChange: undefined | ((newValue: T) => void);
  onChangeNullable?: undefined | ((newValue: T | undefined) => void);
  errorMessage?: string;
  onEnterPressed?: () => void;
  onBlur?: () => void;
  onFocus?: () => void;
  placeholder?: string;
  className?: string;
}

export interface ITextualInputProps extends IBaseInternalInputProps<string> {
  type: 'text';
}

export interface IPasswordInputProps extends IBaseInternalInputProps<string> {
  type: 'password';
}

export interface IMultilineInputProps extends IBaseInternalInputProps<string> {
  type: 'multiline';
}

export type ITextInputInternalProps =
  | ITextualInputProps
  | IPasswordInputProps
  | IMultilineInputProps;

export const TextInputInternal: React.FunctionComponent<
  ITextInputInternalProps
> = props => {
  const { isFocused, htmlId, isPasswordShown, setIsPasswordShown, inputProps } =
    useSetupInputState(props);
  return (
    <div
      className={cn('relative', props.className, {
        ['mt-5']: props.label,
      })}
    >
      <InputLabel {...props} isFocused={isFocused} htmlId={htmlId} />

      <ShowPasswordIcon
        isPasswordShown={isPasswordShown}
        setIsPasswordShown={setIsPasswordShown}
        type={props.type}
        value={inputProps.value}
      />

      {props.type === 'multiline' && <textarea {...inputProps} />}
      {props.type !== 'multiline' && (
        <input
          {...inputProps}
          onKeyDown={
            props.onEnterPressed
              ? onEnterPress(props.onEnterPressed)
              : undefined
          }
          value={inputProps.value || ''}
          type={
            props.type === 'password' && isPasswordShown ? 'text' : props.type
          }
        />
      )}
    </div>
  );
};

function useSetupInputState(props: ITextInputInternalProps) {
  const [value, setValue] = React.useState<string | undefined>();
  React.useEffect(() => {
    if (!isNullOrUndefinedOrEmptyString(props.value) && props.value !== value) {
      setValue(formatValue(props));
    }
  }, [props.value]);
  const [isFocused, setIsFocused] = React.useState(false);
  const [isPasswordShown, setIsPasswordShown] = React.useState(false);
  const [htmlId] = React.useState(uuid.v4());
  const inputProps = {
    disabled: props.disabled,
    placeholder: props.placeholder,
    value,
    id: htmlId,
    onChange: (e: any) => {
      const onChange = getOnChange(props);
      if (onChange) {
        const newValue = e.target.value;
        onChange(newValue);
        setValue(newValue);
      }
    },
    className: cn(
      'border h-auto w-full px-3 py-2 h-[40px] border rounded text-base',
      'bg-transparent',
      {
        ['border-gray-300']: !isFocused && !props.errorMessage,
        ['border-blue-600']: isFocused && !props.errorMessage,
        ['border-red-600']: Boolean(props.errorMessage),
      }
    ),
    onFocus: () => {
      if (props.onFocus) {
        props.onFocus();
      }
      setIsFocused(true);
    },
    onBlur: () => {
      if (props.onBlur) {
        props.onBlur();
      }
      setIsFocused(false);
      setValue(formatValue(props));
    },
  };
  return { isFocused, htmlId, isPasswordShown, setIsPasswordShown, inputProps };
}

interface IInputLabelProps {
  label?: string;
  htmlId: string;
  isFocused: boolean;
  value: string | number | undefined;
  errorMessage?: string;
}

const InputLabel: React.FunctionComponent<IInputLabelProps> = props => {
  return (
    <>
      {props.label && (
        <label
          htmlFor={props.htmlId}
          className={cn(
            'z-1 absolute left-0 top-0 inline-block origin-top-left transform-gpu cursor-text rounded-xl  text-base transition',
            {
              ['translate-x-3 translate-y-2 scale-100 text-slate-700']:
                !props.isFocused && isNullOrUndefinedOrEmptyString(props.value),
              ['text-slate-700']: !isNullOrUndefinedOrEmptyString(props.value),
              ['text-blue-600']: props.isFocused && !props.errorMessage,
              [' -translate-y-5 scale-75']:
                props.isFocused || !isNullOrUndefinedOrEmptyString(props.value),
              ['text-red-600']: Boolean(props.errorMessage),
            }
          )}
        >
          <span>{props.label}</span>
        </label>
      )}
    </>
  );
};

interface IShowPasswordIconProps {
  type: 'text' | 'password' | 'multiline';
  value: string | undefined;
  isPasswordShown: boolean;
  setIsPasswordShown: (isShown: boolean) => void;
}

const ShowPasswordIcon: React.FunctionComponent<
  IShowPasswordIconProps
> = props => {
  return (
    (props.type === 'password' && props.value && (
      <Icon
        name={props.isPasswordShown ? 'AiFillEye' : 'AiFillEyeInvisible'}
        className={'absolute right-8 top-2 cursor-pointer'}
        onClick={() => props.setIsPasswordShown(!props.isPasswordShown)}
      />
    )) ||
    null
  );
};

function formatValue(props: ITextInputInternalProps): string {
  switch (props.type) {
    case 'text':
    case 'password':
    case 'multiline':
      return props.value || '';
    default:
      isNever(props);
  }
}

function getOnChange(
  props: ITextInputInternalProps
): undefined | ((newValue: string | undefined) => void) {
  const onChange = props.onChange;
  if (!onChange) {
    return undefined;
  }
  switch (props.type) {
    case 'text':
    case 'password':
    case 'multiline': {
      const onChangeText = props.onChange;
      if (!onChangeText) {
        return undefined;
      }
      return t => {
        if (t === undefined) {
          t = '';
        }
        onChangeText(t);
        if (props.onChangeNullable) {
          props.onChangeNullable(undefined);
        }
      };
    }
    default:
      isNever(props);
  }
}
